import React from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import GradientButton from 'components/common/GradientButton'
import useGlobal from 'hooks/useGlobal'
import useForm from 'hooks/useForm'
import api from 'utils/api'
import { useSnackbar } from 'notistack'

const useStyles = makeStyles(theme => ({
	button: {
		marginTop: theme.spacing(2),
	},
	fileInput: {
		display: 'none',
	},
	fileLabel: {
		display: 'inline-flex',
		marginTop: theme.spacing(2),
	},
	fileButton: {},
	fileLabelSpan: {
		color: theme.palette.text.hint,
		marginLeft: theme.spacing(2),
		[theme.breakpoints.down('sm')]: {
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			maxWidth: 165,
		},
	},
	uploaded: {
		color: theme.palette.text.primary,
	},
}))

function ConfigPanel() {
	const classes = useStyles()
	const { enqueueSnackbar } = useSnackbar()

	const [config, configActions] = useGlobal(state => state.config, actions => actions.config)

	const { values, setValues, errors, isSubmitting, setIsSubmitting, handleSubmit, handleChange } = useForm(
		{
			wc_title: config.wc_title,
			wc_main_msg: config.wc_main_msg,
			wc_return_msg: config.wc_return_msg,
			wc_cnt_limit: config.wc_cnt_limit,
			wc_main_img: config.wc_main_img,
			wc_main_img_data: null,
		},
		onSubmit,
		validate
	)

	async function onSubmit() {
		try {
			const formData = new FormData()
			formData.append('wc_title', values.wc_title)
			formData.append('wc_main_msg', values.wc_main_msg)
			formData.append('wc_return_msg', values.wc_return_msg)
			formData.append('wc_cnt_limit', values.wc_cnt_limit)
			if (values.wc_main_img_data) {
				formData.append('wc_main_img_orig', config.wc_main_img)
				formData.append('wc_main_img_data', values.wc_main_img_data)
			}
			const { data } = await api.post('/api/config.php', formData, {
				headers: { 'Content-Type': 'multipart/form-data' },
			})
			const wc_main_img = data.wc_main_img ? data.wc_main_img : config.wc_main_img
			configActions.setConfig({ ...data, wc_main_img: wc_main_img })
			enqueueSnackbar('설정을 변경했습니다.', { variant: 'success' })
			setValues(values => ({ ...values, wc_main_img, wc_main_img_data: null }))
		} catch (e) {
			enqueueSnackbar(e.response.data.message || '설정을 변경할 수 없습니다.', { variant: 'error' })
		} finally {
			setIsSubmitting(false)
		}
	}

	function validate(values) {
		let errors = {}
		if (!values.wc_title.trim()) {
			errors.wc_title = '브라우저 제목을 입력하세요.'
		}
		if (!values.wc_main_msg.trim()) {
			errors.wc_main_msg = '메인화면 메시지를 입력하세요.'
		}
		if (!values.wc_return_msg.trim()) {
			errors.wc_return_msg = '박수 성공 메시지를 입력하세요.'
		}
		if (values.wc_cnt_limit === '') {
			errors.wc_cnt_limit = '연속 박수 횟수 한도를 입력하세요.'
		} else if (values.wc_cnt_limit < 0) {
			errors.wc_cnt_limit = '연속 박수 횟수 한도는 0 또는 양수로 입력해야 합니다.'
		}
		return errors
	}

	function handleFileChange(e) {
		const file = e.target.files[0]
		setValues(values => ({ ...values, wc_main_img: file.name, wc_main_img_data: file }))
	}

	return (
		<form onSubmit={handleSubmit} autoComplete="off">
			<TextField
				type="text"
				name="wc_title"
				label="브라우저 제목"
				placeholder="브라우저 상단에 표출되는 제목입니다"
				margin="normal"
				fullWidth
				InputLabelProps={{ shrink: true }}
				helperText={errors.wc_title}
				value={values.wc_title}
				error={!!errors.wc_title}
				onChange={handleChange}
			/>
			<TextField
				type="text"
				name="wc_main_msg"
				label="메인화면 메시지"
				placeholder="메인화면에 표출되는 메시지입니다"
				margin="normal"
				fullWidth
				InputLabelProps={{ shrink: true }}
				helperText={errors.wc_main_msg}
				value={values.wc_main_msg}
				error={!!errors.wc_main_msg}
				onChange={handleChange}
			/>
			<TextField
				type="text"
				name="wc_return_msg"
				label="박수 성공 메시지"
				placeholder="박수 성공시 나타나는 메시지입니다"
				margin="normal"
				fullWidth
				InputLabelProps={{ shrink: true }}
				helperText={errors.wc_return_msg}
				value={values.wc_return_msg}
				error={!!errors.wc_return_msg}
				onChange={handleChange}
			/>
			<TextField
				type="number"
				name="wc_cnt_limit"
				label="연속 박수 횟수 한도"
				placeholder="연속 박수 횟수의 한도를 설정합니다"
				margin="normal"
				fullWidth
				InputLabelProps={{ shrink: true }}
				helperText={errors.wc_cnt_limit || '한도를 설정하고 싶지 않으면 0으로 변경'}
				value={values.wc_cnt_limit}
				error={!!errors.wc_cnt_limit}
				onChange={handleChange}
			/>
			<input
				type="file"
				accept="image/*"
				className={classes.fileInput}
				id="file-button"
				onChange={handleFileChange}
			/>
			<label htmlFor="file-button" className={classes.fileLabel}>
				<GradientButton variant="contained" component="span" className={classes.fileButton}>
					메인 이미지 변경
				</GradientButton>
				<span className={clsx(classes.fileLabelSpan, { [classes.uploaded]: !!values.wc_main_img_data })}>
					{values.wc_main_img}
				</span>
			</label>
			<GradientButton
				type="submit"
				variant="contained"
				color="primary"
				fullWidth
				disabled={isSubmitting}
				className={classes.button}
			>
				설정 변경
			</GradientButton>
		</form>
	)
}

export default ConfigPanel
